import * as React from 'react'
import { theme } from '../theme'
import { AspectOption, aspectOptions, DimensionVariantImage } from './DimensionOptions'

interface DimensionOptionsButtonsProps {
	selectedOption: AspectOption
	onSelect: (option: AspectOption) => void
	dimensionImages?: Record<string, DimensionVariantImage>
	registerContainerRef?: (dimensionId: string, element: HTMLDivElement | null) => void
	onSwitchToAdjustTool?: () => void
}

/**
 * Button-based mode for dimension options
 * Shows a row of buttons with aspect ratio options
 */
export const DimensionOptionsButtons: React.FC<DimensionOptionsButtonsProps> = ({
	selectedOption,
	onSelect,
	dimensionImages = {},
	registerContainerRef,
	onSwitchToAdjustTool,
}) => {
	return (
		<div
			style={{
				display: 'flex',
				gap: theme.spacing.sm,
				padding: theme.spacing.sm,
				backgroundColor: theme.colors.surface,
				borderRadius: theme.borderRadius.sm,
				border: `1px solid ${theme.colors.border}`,
				width: '100%',
				minWidth: 0, // Allows container to shrink below min-content
			}}
		>
			{aspectOptions.map((option) => {
				// Check if the aspect ratio is valid for this option
				const dimensionImage = dimensionImages[option.id]
				const isValid = dimensionImage?.isAspectRatioValid !== false // Default to true if undefined

				return (
					<button
						key={option.id}
						onClick={() => {
							// If the image has an invalid aspect ratio and onSwitchToAdjustTool is provided,
							// redirect to adjust tools, otherwise just select the option
							if (!isValid && dimensionImage && onSwitchToAdjustTool) {
								onSelect(option) // First select this aspect ratio
								onSwitchToAdjustTool() // Then switch to adjust tools
							} else {
								onSelect(option)
							}
						}}
						ref={(el) => {
							// Only call registerContainerRef if el exists and the function is provided
							if (el && registerContainerRef) {
								// Type assertion is needed because we're using a button but the function expects a div
								registerContainerRef(option.id, el as unknown as HTMLDivElement);
							}
						}}
						style={{
							padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
							border:
								option.id === selectedOption.id
									? `2px solid ${theme.colors.primary}`
									: `1px solid ${theme.colors.border}`,
							borderRadius: theme.borderRadius.sm,
							backgroundColor:
								option.id === selectedOption.id ? theme.colors.primary + '10' : theme.colors.surface,
							cursor: 'pointer',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: theme.spacing.xs,
							flex: 1,
							minWidth: 0, // Allows button to shrink below min-content
							overflow: 'hidden', // Ensures content doesn't overflow
							position: 'relative', // For positioning the warning indicator
						}}
					>
						<div
							style={{
								fontSize: theme.typography.sizes.tiny,
								fontWeight: 500,
								color:
									option.id === selectedOption.id ? theme.colors.primary : theme.colors.text.primary,
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								width: '100%',
							}}
						>
							{option.label}
						</div>
						<div
							style={{
								fontSize: theme.typography.sizes.tiny,
								color: theme.colors.text.muted,
								whiteSpace: 'nowrap',
							}}
						>
							{option.targetWidth}×{option.targetHeight}
						</div>

						{/* Warning indicator for invalid aspect ratio in non-preview mode */}
						{!isValid && dimensionImage && (
							<div
								style={{
									position: 'absolute',
									top: '4px',
									right: '4px',
									backgroundColor: 'rgba(255, 255, 255, 0.8)',
									borderRadius: '50%',
									width: '16px',
									height: '16px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: '#f39c12', // Warning color
									fontSize: '12px',
									zIndex: 2,
								}}
								title="Image doesn't match the expected aspect ratio"
							>
								⚠️
							</div>
						)}
					</button>
				)
			})}
		</div>
	)
}
